/**
 * Screenshot annotation surface.
 *
 * Shapes are kept as a list and the canvas is fully redrawn on every change,
 * which makes undo trivial and keeps the blackout tool honest: because the
 * export re-renders from the original bitmap each time, a redaction is baked in
 * at flatten time and there is no path that leaks the pixels underneath.
 */

export type Tool = 'rect' | 'arrow' | 'pen' | 'blackout';

interface BaseShape {
  colour: string;
  lineWidth: number;
}
interface RectShape extends BaseShape {
  kind: 'rect';
  x: number; y: number; w: number; h: number;
}
interface ArrowShape extends BaseShape {
  kind: 'arrow';
  x1: number; y1: number; x2: number; y2: number;
}
interface PenShape extends BaseShape {
  kind: 'pen';
  points: Array<{ x: number; y: number }>;
}
interface BlackoutShape {
  kind: 'blackout';
  x: number; y: number; w: number; h: number;
}
type Shape = RectShape | ArrowShape | PenShape | BlackoutShape;

export interface Annotator {
  canvas: HTMLCanvasElement;
  setTool(tool: Tool): void;
  getTool(): Tool;
  undo(): void;
  clear(): void;
  hasEdits(): boolean;
  /** Flattened PNG data URL: original bitmap plus every shape. */
  toDataUrl(): string;
  destroy(): void;
}

export interface AnnotatorOptions {
  image: HTMLImageElement;
  accent: string;
  /** Called whenever the shape list changes, so the toolbar can update. */
  onChange?: () => void;
}

const ARROW_HEAD = 16;

export function createAnnotator(options: AnnotatorOptions): Annotator {
  const { image, accent } = options;

  const canvas = document.createElement('canvas');
  canvas.className = 'preview__canvas';
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', 'Screenshot — drag to annotate');

  const context = canvas.getContext('2d');

  const shapes: Shape[] = [];
  let tool: Tool = 'rect';
  let draft: Shape | null = null;
  let activePointer: number | null = null;

  // Scale annotation strokes with the screenshot so they read the same on a
  // 4K capture as on a phone-sized one.
  const lineWidth = Math.max(2, Math.round(canvas.width / 400));

  function draw(): void {
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    for (const shape of shapes) paint(context, shape);
    if (draft) paint(context, draft);
  }

  function paint(ctx: CanvasRenderingContext2D, shape: Shape): void {
    ctx.save();

    if (shape.kind === 'blackout') {
      ctx.fillStyle = '#000';
      ctx.fillRect(shape.x, shape.y, shape.w, shape.h);
      ctx.restore();
      return;
    }

    ctx.strokeStyle = shape.colour;
    ctx.fillStyle = shape.colour;
    ctx.lineWidth = shape.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (shape.kind === 'rect') {
      ctx.strokeRect(shape.x, shape.y, shape.w, shape.h);
    } else if (shape.kind === 'arrow') {
      const { x1, y1, x2, y2 } = shape;
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const head = Math.max(ARROW_HEAD, shape.lineWidth * 4);

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 7), y2 - head * Math.sin(angle - Math.PI / 7));
      ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 7), y2 - head * Math.sin(angle + Math.PI / 7));
      ctx.closePath();
      ctx.fill();
    } else {
      const [first, ...rest] = shape.points;
      if (!first) {
        ctx.restore();
        return;
      }
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (const point of rest) ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }

    ctx.restore();
  }

  /** Maps a pointer position from CSS pixels to the canvas's own coordinates. */
  function toCanvasPoint(event: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / (rect.width || 1);
    const scaleY = canvas.height / (rect.height || 1);
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  function onPointerDown(event: PointerEvent): void {
    if (activePointer !== null || !event.isPrimary) return;
    activePointer = event.pointerId;
    canvas.setPointerCapture(event.pointerId);

    const { x, y } = toCanvasPoint(event);

    if (tool === 'pen') {
      draft = { kind: 'pen', points: [{ x, y }], colour: accent, lineWidth };
    } else if (tool === 'arrow') {
      draft = { kind: 'arrow', x1: x, y1: y, x2: x, y2: y, colour: accent, lineWidth };
    } else if (tool === 'blackout') {
      draft = { kind: 'blackout', x, y, w: 0, h: 0 };
    } else {
      draft = { kind: 'rect', x, y, w: 0, h: 0, colour: accent, lineWidth };
    }
    draw();
  }

  function onPointerMove(event: PointerEvent): void {
    if (activePointer !== event.pointerId || !draft) return;
    const { x, y } = toCanvasPoint(event);

    if (draft.kind === 'pen') {
      draft.points.push({ x, y });
    } else if (draft.kind === 'arrow') {
      draft.x2 = x;
      draft.y2 = y;
    } else {
      draft.w = x - draft.x;
      draft.h = y - draft.y;
    }
    draw();
  }

  function onPointerUp(event: PointerEvent): void {
    if (activePointer !== event.pointerId) return;
    activePointer = null;
    try {
      canvas.releasePointerCapture(event.pointerId);
    } catch {
      /* pointer already released */
    }

    if (draft && !isDegenerate(draft)) {
      shapes.push(draft);
      options.onChange?.();
    }
    draft = null;
    draw();
  }

  /** Drops accidental click-without-drag marks. */
  function isDegenerate(shape: Shape): boolean {
    if (shape.kind === 'pen') return shape.points.length < 2;
    if (shape.kind === 'arrow') {
      return Math.hypot(shape.x2 - shape.x1, shape.y2 - shape.y1) < 6;
    }
    return Math.abs(shape.w) < 6 || Math.abs(shape.h) < 6;
  }

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);

  draw();

  return {
    canvas,
    setTool(next) {
      tool = next;
    },
    getTool: () => tool,
    undo() {
      if (shapes.length === 0) return;
      shapes.pop();
      draw();
      options.onChange?.();
    },
    clear() {
      shapes.length = 0;
      draw();
      options.onChange?.();
    },
    hasEdits: () => shapes.length > 0,
    toDataUrl() {
      draft = null;
      draw();
      return canvas.toDataURL('image/png');
    },
    destroy() {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
    },
  };
}
